import { MULTI_THREAD } from 'rx-player/experimental/features';
import { EMBEDDED_WORKER } from 'rx-player/experimental/features/embeds';
import {
  DASH,
  DEBUG_ELEMENT,
  DIRECTFILE,
  EME,
  HTML_SAMI_PARSER,
  HTML_TTML_PARSER,
  HTML_VTT_PARSER,
  NATIVE_SAMI_PARSER,
  NATIVE_TTML_PARSER,
  NATIVE_VTT_PARSER,
  SMOOTH,
} from 'rx-player/features';
import RxMinimal from 'rx-player/minimal';
import type { IConstructorOptions } from 'rx-player/types';

import { logger } from '@canalplus/oneplayer-utils';

import RxPlayerWrapper from './player';

/**
 * Initialize an RxPlayer with all the potential features wanted by the
 * OnePlayer.
 * @param playerOptions - Options for the RxPlayer instantiation
 * @param initOptions - Options when initializing an RxPlayer.
 * @param initOptions.enableMultiThreading - If `true`, the RxPlayer will be
 * initialized with multithreading capabilities.
 * @param initOptions.shouldImportLocalManifestFeature - If `true`, the RxPlayer will be
 * initialized with local manifest capabilities.
 * @returns - An Object with both an instance of the RxPlayer and its class.
 */
export async function initializeRxPlayer(
  playerOptions: IConstructorOptions,
  initOptions: {
    enableMultiThreading?: boolean;
    shouldImportLocalManifestFeature?: boolean;
  },
): Promise<RxPlayerWrapper> {
  // This one as a dynamic import as only Canal+ set-top boxes should need that
  // feature for now (as opposed to the other features that 95% of platforms
  // want).
  const localManifestExperimentalFeature =
    initOptions.shouldImportLocalManifestFeature
      ? await import(
          /* webpackExports: ["LOCAL_MANIFEST"] */
          /* webpackChunkName: "rx-player-experimental-features-local-manifest" */ 'rx-player/experimental/features'
        ).then((features) => [features.LOCAL_MANIFEST])
      : [];

  RxMinimal.addFeatures([
    DASH,
    DEBUG_ELEMENT,
    DIRECTFILE,
    EME,
    HTML_SAMI_PARSER,
    HTML_TTML_PARSER,
    HTML_VTT_PARSER,
    NATIVE_SAMI_PARSER,
    NATIVE_TTML_PARSER,
    NATIVE_VTT_PARSER,
    SMOOTH,
    ...localManifestExperimentalFeature,
  ]);
  const rxPlayer = new RxMinimal(playerOptions);

  if (__DEV__) {
    window.$setRxPlayerLogLevel = (logLevel: string): void => {
      RxMinimal.LogLevel = logLevel;
    };
  }

  if (initOptions.enableMultiThreading) {
    try {
      RxMinimal.addFeatures([MULTI_THREAD]);
      rxPlayer.attachWorker({
        workerUrl: EMBEDDED_WORKER,
      });
    } catch (err) {
      logger.warn(
        'OnePlayerCoreRxPlayer > initializeRxPlayer > Failed to initialize worker',
        err,
      );
    }
  }
  return new RxPlayerWrapper(rxPlayer);
}
